<script setup lang="ts">
import { storeToRefs } from 'pinia';
import ChatInput, { AttachmentSelected } from './ChatInput.vue';
import TextSubmitForm from '@/components/TextSubmitForm.vue';
import { useChatWidget, InteractionView } from '@/stores/useChatWidget';
import { useVoiceTranscript } from '@/stores/useVoiceTranscript';
import { useWidgetConversation } from '@/stores/useWidgetConversation';
import { computed, watch, ref, unref, nextTick } from 'vue';
import { logger } from '@/common/api/logger';
import { parseErrorMessage, getApiErrorCode } from '@/common/error';
import { captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import { randomId } from '@/common/bot/lib';
import { ExternalBotEvent, ResolutionType } from '@/common/bot/enum';
import { useOpenRequestCustomisation } from '../composables/useOpenRequestCustomisation';
import { useEndChatButtonCustomisation } from '../composables/useEndChatButtonCustomisation';
import { CallStatus, useTwilioVoiceCallManager } from '@/components/VoiceCallIndicator/useTwilioVoiceCallManager';
import { useLandingAiVoiceCallManager } from '@/components/VoiceCallIndicator/useLandingAiVoiceCallManager';
import { useVoiceAiHandover } from '@/stores/useVoiceAiHandover';
import PlsButton1 from '@/components/PlsButton1.vue';
import VoiceCallIndicator from '@/components/VoiceCallIndicator/VoiceCallIndicator.vue';
import NewCsat from '@/views/NewCsat/NewCsat.vue';
import {
  ConnectPage,
  ConnectRequestType,
  ConnectWizardEvent,
  isConnectToSupportEvent,
  type VisitorInfoSubmittedEvent,
} from '@/library/support-wizard';
import { PleasedChatEvent } from '@/library/pleased-chat-controller/usePleasedChatController';
import ChatWidgetLogin from './LoginForm.vue';
import ChatWidgetGuestForm from './GuestForm';
import type { VisitorInfoFilled } from '@/common/types';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { useI18n } from '@/plugins/i18n';
import { useBotUser } from '@/stores/useBotUser';
import { isLandingEntrySupported, resolveLandingEntryUserId } from '@/common/landingEntry.utils';
import { getVoiceAiFallbackConfig } from '@/common/voice-ai/voiceAi.utils';
import { PleasedApi } from '@/pleased-sdk';
import { UserFileForAIEvent } from '@/common/bot/types';

interface Props {
  isOpened?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isOpened: false,
});

// Emits
const emit = defineEmits<{
  (e: 'onSubmit', message: string): void;
  (e: 'onFileUpload', attachment: AttachmentSelected): void;
  (e: 'onRefresh'): void;
  (e: 'typing', isTyping: boolean): void;
}>();

const isVoiceCallEndDisabled = ref(false);

// Store
const { t } = useI18n();
const chatWidget = useChatWidget();
const { interactionView, lastViewBeforeClose } = storeToRefs(chatWidget);

const connectConfigStore = useConnectConfig();
const { connectConfig } = storeToRefs(connectConfigStore);

const brandConfigStore = useBrandConfig();
const { loading: isBrandConfigLoading } = storeToRefs(brandConfigStore);

const userStore = useBotUser();
const { isAuthenticated, user } = storeToRefs(userStore);

const widgetConversation = useWidgetConversation();
const {
  checkCanConnectToAI,
  submitUserMessageForAI,
  submitFlowExternalAction,
  endChat,
  onAiFlowStart,
  onEndLiveChat,
  onEndVoiceCall,
  onEndAiChat,
  onLandingAiVoiceStart,
  startLandingAiVoiceCall,
  closeChatbot,
  openLandingOverlay,
  showVoiceAiNewChatError,
} = widgetConversation;
// TODO: add state to exposed params of supportWizard, use for description blocks
const { aiFlow, feedbackState, supportWizard, startFlowRunning } = storeToRefs(widgetConversation);

// Customisation
const { customisation: openRequestCustomisation, placeholderText: openRequestPlaceholder } =
  useOpenRequestCustomisation();

const { endChatCustomisation } = useEndChatButtonCustomisation();

const supportRequestPlaceholder = computed(() => {
  return connectConfig.value?.descriptionMessage || t('global.describe');
});

/** Guest sees the description field only on the open-request step (after choosing Open Request in connect options). */
const isGuestOpenRequestFlow = computed(() => interactionView.value === InteractionView.OPEN_REQUEST_DESCRIPTION);

const pleasedChat = computed(() => ({
  active: supportWizard.value.support.chat.active,
  chat: unref(supportWizard.value.support.chat.chat),
  voice: supportWizard.value.support.voice,
  isConnectOngoing: supportWizard.value.isConnectOngoing,
}));

const voiceChatId = computed(() => pleasedChat.value.voice.voiceChatId as unknown as number);
const savedDescription = computed(() => supportWizard.value.description as unknown as string);
const showRefreshButton = computed(() => {
  if (interactionView.value === InteractionView.LANDING) {
    return false;
  }

  // TODO: add check from customisation as well
  return !pleasedChat.value.active;
});

const isLandingMode = computed(
  () =>
    isLandingEntrySupported({
      userId: resolveLandingEntryUserId(isAuthenticated.value, user.value.id),
    }) && interactionView.value === InteractionView.LANDING
);

const { voiceCallDuration, currentStatus, hangUp } = useTwilioVoiceCallManager({
  voiceChatId,
  // on disconnect, from agent/user – to verify
  onDisconnect: (data) => {
    // Only logged in users can leave CSAT
    if (data.status === CallStatus.CONNECTED && isAuthenticated.value) {
      feedbackState.value = {
        id: data.voiceChatId,
        type: 'voice',
      };

      chatWidget.setInteractionView(InteractionView.CSAT);
    } else {
      isVoiceCallEndDisabled.value = false;
      chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
    }
  },
});

const isLandingAiVoiceCallEndDisabled = ref(false);
const landingAiAudioRef = ref<HTMLAudioElement | null>(null);

// Holds the just-finished Voice AI session so CSAT can be shown after "End chat".
const pendingVoiceAiCsatId = ref<string | null>(null);

const voiceTranscript = useVoiceTranscript();
const voiceAiHandover = useVoiceAiHandover();

const {
  voiceCallDuration: landingAiVoiceCallDuration,
  isMuted: isLandingAiVoiceMuted,
  start: startLandingAiVoiceCallSession,
  hangUp: hangUpLandingAiVoiceCall,
  toggleMute: toggleLandingAiVoiceMute,
} = useLandingAiVoiceCallManager({
  onDisconnect: ({ status, handoffReason, aiSessionId }) => {
    isLandingAiVoiceCallEndDisabled.value = false;
    voiceTranscript.finalize();
    if (handoffReason) {
      // Remember the session so the picked channel can be reported once created.
      voiceAiHandover.rememberHandoffSession(aiSessionId);
      supportWizard.value.startWizard({
        connectToAgentConfig: { ...getVoiceAiFallbackConfig() },
      });
      return;
    }

    pendingVoiceAiCsatId.value = status === CallStatus.CONNECTED ? (aiSessionId ?? null) : null;

    chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
  },
  onError: (error) => {
    logger.error('[LANDING_AI_VOICE] Error', { error: parseErrorMessage(error) });
    captureEventException(error, ErrorTrackingEvent.LandingVoiceAi);
    isLandingAiVoiceCallEndDisabled.value = false;
    voiceTranscript.finalize();
    pendingVoiceAiCsatId.value = null;

    if (getApiErrorCode(error) !== undefined) {
      showVoiceAiNewChatError(error);
      return;
    }

    chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
  },
  onMessage: (text, who, metadata) => voiceTranscript.handleMessage(text, who, metadata),
});

const handleLandingAiVoiceStart = () => {
  nextTick(() => {
    if (!landingAiAudioRef.value) {
      logger.error('[LANDING_AI_VOICE] Audio element not available');
      captureEventException(
        new Error('Landing Voice AI audio element not available'),
        ErrorTrackingEvent.LandingVoiceAi
      );
      chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
      return;
    }

    pendingVoiceAiCsatId.value = null;
    voiceTranscript.reset();
    startLandingAiVoiceCallSession(landingAiAudioRef.value);
  });
};

onLandingAiVoiceStart(handleLandingAiVoiceStart);

// TODO: move to composables, split by view type
const isInputDisabled = computed(() => {
  if (!props.isOpened) return true;

  const isBlockedByAIFlow = aiFlow.value.active && aiFlow.value.userInputDisabled;
  const isBlockedByConnectOngoing = supportWizard.value.isConnectOngoing;
  const isBlockedByStartFlow = Boolean(startFlowRunning.value);
  const isBlockedBySupportLoading = supportWizard.value.support.supportLoading;

  return Boolean(isBlockedByAIFlow || isBlockedByConnectOngoing || isBlockedByStartFlow || isBlockedBySupportLoading);
});

const showFileUpload = computed(() => {
  if (!props.isOpened) return false;
  const isAllowedByAIFlow = aiFlow.value.active && !aiFlow.value.userFileUploadDisabled;
  const isAllowedByLiveChat = pleasedChat.value.active;

  return Boolean(isAllowedByAIFlow || isAllowedByLiveChat);
});

const applyAiEligibilityInteractionView = () => {
  const isLiveChatActive = pleasedChat.value.active;
  const isAiEligible = checkCanConnectToAI();
  const isShowingInputView = interactionView.value === InteractionView.INPUT;

  if (interactionView.value === InteractionView.LANDING) {
    return;
  }

  // Hide input if:
  // - No live chat active
  // - Currently showing input
  // - AI flow not eligible
  if (!isLiveChatActive && isShowingInputView && !isAiEligible) {
    chatWidget.setInteractionView(InteractionView.NONE);
  }
};

// TODO: add re-check if user is changed or refactor to better dependency tracking
watch(
  () => props.isOpened,
  (isOpened, wasOpened) => {
    if (isOpened) {
      // Overlay is being opened
      if (lastViewBeforeClose.value) {
        // Restore only if no newer view was set while overlay was closed.
        if (interactionView.value !== InteractionView.INPUT) {
          lastViewBeforeClose.value = null;
          return;
        }

        // Restore previous view if it was saved
        chatWidget.setInteractionView(lastViewBeforeClose.value);
        lastViewBeforeClose.value = null;

        return;
      }

      // Defer until brand config is ready; `checkCanConnectToAI` depends on it.
      if (!isBrandConfigLoading.value) {
        applyAiEligibilityInteractionView();
      }

      return;
    }

    if (wasOpened && !isOpened) {
      // Overlay is being closed
      // Save current view if it's not INPUT or LANDING
      if (interactionView.value !== InteractionView.INPUT && interactionView.value !== InteractionView.LANDING) {
        lastViewBeforeClose.value = interactionView.value;
      }

      if (interactionView.value !== InteractionView.LANDING) {
        chatWidget.setInteractionView(InteractionView.INPUT);
      }

      return;
    }
  }
);

watch(isBrandConfigLoading, (loading) => {
  if (loading || !props.isOpened) return;

  applyAiEligibilityInteractionView();
});

const { onWizardEvent } = supportWizard.value;

supportWizard.value.support.chat.onChat(({ event }) => {
  if (event === PleasedChatEvent.CLOSED) {
    chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
  }
});

// TODO: refactor reaction to events into composable which will handle events from logic layer
// Handle view changes based on connect support event
onWizardEvent((event) => {
  // Set Open request description to confirm it before submitting
  if (
    isConnectToSupportEvent(event) &&
    event.payload.type === ConnectRequestType.OPEN_REQUEST &&
    event.payload.payload?.confirmation
  ) {
    chatWidget.setInteractionView(InteractionView.OPEN_REQUEST_DESCRIPTION);
    return;
  }

  // Set view based on connect request type
  if (isConnectToSupportEvent(event) && !event.payload.payload?.confirmation) {
    const { type } = event.payload;
    if (type === ConnectRequestType.CHAT) {
      chatWidget.setInteractionView(InteractionView.LIVE_CHAT_INDICATOR);
    } else if (type === ConnectRequestType.VOICE) {
      chatWidget.setInteractionView(InteractionView.VOICE_CHAT_INDICATOR);
    } else if (type === ConnectRequestType.VOICE_AI) {
      startLandingAiVoiceCall();
    } else if (type === ConnectRequestType.OPEN_REQUEST) {
      chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
    }
  }
});

onAiFlowStart(() => {
  chatWidget.setInteractionView(InteractionView.LIVE_CHAT_INDICATOR);
});

onEndAiChat(() => {
  chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
});

onEndLiveChat(({ resolution }) => {
  if (resolution === ResolutionType.YES) {
    endChat();
  }
});

// Manual end voice from user
onEndVoiceCall(({ resolution }) => {
  isVoiceCallEndDisabled.value = false;
  const isConnected = currentStatus.value === CallStatus.CONNECTED;

  if (resolution === ResolutionType.YES) {
    hangUp();

    if (!isConnected || !isAuthenticated.value) {
      chatWidget.setInteractionView(InteractionView.END_CHAT_BUTTON);
      return;
    }

    // Only logged in users can leave CSAT
    feedbackState.value = {
      id: voiceChatId.value,
      type: 'voice',
    };

    chatWidget.setInteractionView(InteractionView.CSAT);
  }
});

// Event handlers
function submitMessage(message: string) {
  if (pleasedChat.value.active) {
    pleasedChat.value.chat?.sendMessage(message);
    return;
  }

  submitUserMessageForAI(message);
}

async function submitAiFileUpload(file: File) {
  const attachment = await PleasedApi.uploadAttachment(file);
  // send message to ai with attachment url

  const event: UserFileForAIEvent = {
    type: ExternalBotEvent.USER_FILE_FOR_AI,
    id: randomId(),
    payload: {
      fileName: attachment.fileName,
      contentType: attachment.contentType,
      fileUrl: attachment.url,
      attachmentId: attachment.id,
    },
  };

  submitFlowExternalAction(event);
}

function submitFile({ file }: AttachmentSelected) {
  if (pleasedChat.value.active) {
    pleasedChat.value.chat?.sendFile(file);
    return;
  }

  // AI Flow
  submitAiFileUpload(file);
}

function setTypingStatus(isTyping: boolean) {
  pleasedChat.value.chat?.setTypingStatus(isTyping);
}

const handleLandingActivate = () => {
  openLandingOverlay();
};

/**
 * Requests restart-chat confirmation (resolution). Submits RESTART_CHAT_RESOLUTION so the flow
 * shows a "Confirm restart => Yes/No" message. Yes restarts the conversation without closing
 * the overlay; No keeps the user in the current flow. See docs/flows/restart-chat.md.
 */
const handleRestartChat = () => {
  submitFlowExternalAction({ type: ExternalBotEvent.RESTART_CHAT_RESOLUTION, id: randomId() });
};

const handleSupportRequestDescriptionSubmit = (description: string) => {
  // emit('onSupportRequestDescriptionSubmit', description);
  supportWizard.value.submitDescriptionSubmitted({ description });
};

const handleOpenRequestDescriptionSubmit = (description: string) => {
  supportWizard.value.submitOpenRequestDescriptionSubmitted({ description });
};

const handleDescriptionCancel = () => {
  chatWidget.setInteractionView(InteractionView.INPUT);
  submitFlowExternalAction({ type: ExternalBotEvent.CANCEL_SUPPORT_REQUEST, id: randomId() });
};

const handleVoiceCallDisconnect = () => {
  isVoiceCallEndDisabled.value = true;
  submitFlowExternalAction({ type: ExternalBotEvent.END_VOICE_CALL, id: randomId() });
};

const handleLandingAiVoiceCallDisconnect = () => {
  isLandingAiVoiceCallEndDisabled.value = true;
  hangUpLandingAiVoiceCall();
};

const handleEndChat = () => {
  voiceTranscript.reset();

  // After a Voice AI call, show CSAT first instead of ending immediately.
  if (pendingVoiceAiCsatId.value) {
    feedbackState.value = {
      id: pendingVoiceAiCsatId.value,
      type: 'voiceAi',
    };
    pendingVoiceAiCsatId.value = null;
    chatWidget.setInteractionView(InteractionView.CSAT);
    return;
  }

  endChat();
};

// CSAT handlers
const csatEntityId = computed(() => (feedbackState.value?.id ? String(feedbackState.value.id) : undefined));
const csatEntityType = computed(() => feedbackState.value?.type || undefined);
const showCsat = computed(() => {
  return interactionView.value === InteractionView.CSAT;
});

// After any CSAT, reset the conversation/view and close the widget overlay.
const finishCsat = () => {
  feedbackState.value = {};
  closeChatbot();
};

const handleCsatSubmit = () => {
  finishCsat();
};

const handleCsatSkip = () => {
  finishCsat();
};

const handleCsatLowRatingSupport = () => {
  chatWidget.setInteractionView(InteractionView.NONE);
  submitFlowExternalAction({ type: ExternalBotEvent.LOW_CSAT_CONNECT, id: randomId() });
};

const handleLoginSuccess = async () => {
  // LoginForm may sync Pinia first; onAuth then runs submitWizardAction(loginSuccessful) while wizard is Ongoing.
  if (!isAuthenticated.value) {
    supportWizard.value.submitWizardAction({ type: ConnectWizardEvent.loginSuccessful });
  }

  const shouldContinueAiConnect = Boolean(connectConfig.value?.aiConversation) || checkCanConnectToAI();

  if (shouldContinueAiConnect) {
    submitFlowExternalAction({
      type: ExternalBotEvent.USER_LOGIN,
      id: randomId(),
      payload: { supportOptions: connectConfig.value },
    });
  }
};

const handleContinueAsGuest = () => {
  supportWizard.value.submitWizardAction({ type: ConnectWizardEvent.continueAsGuest });
};

const handleGuestFormSubmit = async (payload: VisitorInfoFilled) => {
  const { visitorInfo, description } = payload;

  const event: VisitorInfoSubmittedEvent = {
    type: ConnectWizardEvent.guestInfoFilled,
    page: ConnectPage.guestForm,
    payload: {
      description,
      visitorInfo: visitorInfo
        ? {
            displayName: visitorInfo.displayName,
            email: visitorInfo.email,
            name: visitorInfo.name,
            phone: visitorInfo.phone,
            isGuest: visitorInfo.isGuest ?? true,
          }
        : undefined,
    },
  };

  supportWizard.value.submitWizardAction(event);

  const shouldContinueAiConnect = Boolean(connectConfig.value?.aiConversation) && checkCanConnectToAI();

  if (shouldContinueAiConnect) {
    submitFlowExternalAction({
      type: ExternalBotEvent.GUEST_LOGIN,
      id: randomId(),
      payload: { supportOptions: connectConfig.value },
    });
  }
};

const handleGuestFormCancel = () => {
  handleDescriptionCancel();
};

const handleLoginCancelSupportRequest = () => {
  handleDescriptionCancel();
};

const handleGuestFormBackToLogin = () => {
  supportWizard.value.submitWizardAction({ type: ConnectWizardEvent.goBack });
};
</script>

<template>
  <div class="chat-interaction">
    <audio id="ai-audio" ref="landingAiAudioRef" class="landing-ai-voice-audio" aria-hidden="true" />

    <!-- Input view -->

    <template
      v-if="
        interactionView === InteractionView.LANDING ||
        interactionView === InteractionView.INPUT ||
        interactionView === InteractionView.LIVE_CHAT_INDICATOR
      "
    >
      <ChatInput
        :disabled="isInputDisabled"
        :landing-mode="isLandingMode"
        :show-file-upload="showFileUpload"
        :show-refresh="showRefreshButton"
        :is-opened="isOpened"
        @submit-message="submitMessage"
        @file-upload="submitFile"
        @restart-chat="handleRestartChat"
        @typing="setTypingStatus"
        @landing-activate="handleLandingActivate"
      />
    </template>

    <!--
     TODO: we need to refactor usage of this component for Open Request of guest users
      rather it would be better to handle inside OpenRequest component itself 2 modes logged in and guest
    -->
    <ChatWidgetGuestForm
      v-else-if="
        interactionView === InteractionView.GUEST_FORM ||
        (interactionView === InteractionView.OPEN_REQUEST_DESCRIPTION && !isAuthenticated)
      "
      :key="isGuestOpenRequestFlow ? 'guest-open-request' : 'guest-identity'"
      :is-open-request-flow="isGuestOpenRequestFlow"
      @submit:detail="handleGuestFormSubmit"
      @support-request:cancel="handleGuestFormCancel"
      @back-to-login="handleGuestFormBackToLogin"
      @close-chatbot="closeChatbot"
    />

    <!-- Open request description view -->
    <TextSubmitForm
      v-else-if="interactionView === InteractionView.OPEN_REQUEST_DESCRIPTION"
      :initial-value="savedDescription"
      :placeholder="openRequestPlaceholder"
      :customisation="openRequestCustomisation"
      @submit="handleOpenRequestDescriptionSubmit"
      @cancel="handleDescriptionCancel"
    />

    <!-- Support request description view -->
    <TextSubmitForm
      v-else-if="interactionView === InteractionView.SUPPORT_REQUEST_DESCRIPTION"
      :initial-value="savedDescription"
      :placeholder="supportRequestPlaceholder"
      :customisation="openRequestCustomisation"
      @submit="handleSupportRequestDescriptionSubmit"
      @cancel="handleDescriptionCancel"
    />

    <template v-else-if="interactionView === InteractionView.END_CHAT_BUTTON">
      <div class="end-chat-button-container">
        <PlsButton1
          :text="t('global.end_chat')"
          :background-color="endChatCustomisation.backgroundColor"
          :text-color="endChatCustomisation.textColor"
          :border-color="endChatCustomisation.borderColor"
          class="end-chat-button"
          data-testid="end-chat-button"
          @click="handleEndChat"
        />
      </div>
    </template>

    <!-- Twilio agent voice call indicator -->
    <VoiceCallIndicator
      v-else-if="interactionView === InteractionView.VOICE_CHAT_INDICATOR"
      :disabled="isVoiceCallEndDisabled"
      :call-duration="voiceCallDuration"
      @end-call="handleVoiceCallDisconnect"
    />

    <!-- Landing Voice AI call indicator -->
    <VoiceCallIndicator
      v-else-if="interactionView === InteractionView.LANDING_AI_VOICE_INDICATOR"
      :disabled="isLandingAiVoiceCallEndDisabled"
      :call-duration="landingAiVoiceCallDuration"
      show-mute-button
      :is-muted="isLandingAiVoiceMuted"
      @toggle-mute="toggleLandingAiVoiceMute"
      @end-call="handleLandingAiVoiceCallDisconnect"
    />

    <!-- CSAT Feedback -->
    <NewCsat
      v-else-if="showCsat"
      :entity-id="csatEntityId"
      :entity-type="csatEntityType"
      @submit="handleCsatSubmit"
      @skip="handleCsatSkip"
      @low-rating-support="handleCsatLowRatingSupport"
    />

    <ChatWidgetLogin
      v-else-if="interactionView === InteractionView.LOGIN"
      :is-guest-allowed="!connectConfig?.loginRequired && !connectConfig?.aiMandatoryAccountLogin"
      @on-login="handleLoginSuccess"
      @continue-as-guest="handleContinueAsGuest"
      @cancel-support-request="handleLoginCancelSupportRequest"
    />

    <template v-else-if="interactionView === InteractionView.NONE" />
  </div>
</template>

<style lang="scss" scoped>
.chat-interaction {
  width: 100%;
}

.end-chat-button-container {
  display: flex;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.end-chat-button {
  width: auto;
  max-width: min(40vw, 200px);
  min-width: min(40vw, 200px);

  @media (min-width: 768px) {
    max-width: 200px;
  }
}

.landing-ai-voice-audio {
  display: none;
}
</style>
